import _dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import minMax from 'dayjs/plugin/minMax';
import localeData from 'dayjs/plugin/localeData';

type Dayjs = _dayjs.Dayjs;

_dayjs.extend(utc);
_dayjs.extend(timezone);
_dayjs.extend(customParseFormat);
_dayjs.extend(isBetween);
_dayjs.extend(isSameOrBefore);
_dayjs.extend(isSameOrAfter);
_dayjs.extend(minMax);
_dayjs.extend(localeData);

type ExtendedDayjs = typeof _dayjs & {
  __extended: true;
  localeData: typeof localeData;
};

const localDayjs: ExtendedDayjs = _dayjs as unknown as ExtendedDayjs;

export function nowUtc(): Date {
  return nowUtcDayjs().toDate();
}

// Mock dayjs.utc to mock all "now" datetimes see test file
export function nowUtcDayjs(): Dayjs {
  return localDayjs.utc();
}

export function nowCdt(): Date {
  return nowCdtDayjs().toDate();
}

export function nowCdtDayjs(): Dayjs {
  return nowUtcDayjs().tz('America/Chicago');
}

export function dayjsCdt(date: _dayjs.ConfigType): Dayjs {
  return localDayjs.tz(date, 'America/Chicago');
}

export function isValidYear(year: number): boolean {
  const date = localDayjs(`${year}-01-01`, 'YYYY-MM-DD', true);
  return date.isValid();
}

// fun thing to make Zod happy
// z.instanceof(DayjsCtor)
export const DayjsCtor = localDayjs as unknown as typeof _dayjs.Dayjs;

export const diffInWeeks = (fromDate: Dayjs, toDate: Dayjs) => {
  return toDate.diff(fromDate, 'week');
};

const dayjs: ExtendedDayjs = localDayjs;

declare module 'dayjs' {
  interface Dayjs {
    // utc
    utc(keepLocalTime?: boolean): _dayjs.Dayjs;
    local(): _dayjs.Dayjs;
    isUTC(): boolean;
    utcOffset(offset: number | string, keepLocalTime?: boolean): _dayjs.Dayjs;

    // timezone
    tz(timezone?: string, keepLocalTime?: boolean): _dayjs.Dayjs;
    offsetName(type?: 'short' | 'long'): string | undefined;

    // isBetween
    isBetween(
      a: _dayjs.ConfigType,
      b: _dayjs.ConfigType,
      c?: _dayjs.ConfigType | null,
      d?: '()' | '[]' | '[)' | '(]'
    ): boolean;

    // isSameOrAfter
    isSameOrAfter(date?: _dayjs.ConfigType, unit?: _dayjs.OpUnitType): boolean;

    // isSameOrBefore
    isSameOrBefore(date?: _dayjs.ConfigType, unit?: _dayjs.OpUnitType): boolean;
  }

  // utc
  export function utc(
    config?: _dayjs.ConfigType,
    format?: string,
    strict?: boolean
  ): _dayjs.Dayjs;

  // timezone
  interface DayjsTimezone {
    (date?: _dayjs.ConfigType, timezone?: string): _dayjs.Dayjs;
    (date: _dayjs.ConfigType, format: string, timezone?: string): _dayjs.Dayjs;
    guess(): string;
    setDefault(timezone?: string): void;
  }

  // @ts-expect-error fixes downstream issues with dayjs timezone
  const tz: DayjsTimezone;

  // minMax
  export function max(dayjs: [_dayjs.Dayjs, ..._dayjs.Dayjs[]]): _dayjs.Dayjs;
  export function max(noDates: never[]): null;
  export function max(maybeDates: _dayjs.Dayjs[]): _dayjs.Dayjs | null;

  export function max(
    ...dayjs: [_dayjs.Dayjs, ..._dayjs.Dayjs[]]
  ): _dayjs.Dayjs;
  export function max(...noDates: never[]): null;
  export function max(...maybeDates: _dayjs.Dayjs[]): _dayjs.Dayjs | null;

  export function min(dayjs: [_dayjs.Dayjs, ..._dayjs.Dayjs[]]): _dayjs.Dayjs;
  export function min(noDates: never[]): null;
  export function min(maybeDates: _dayjs.Dayjs[]): _dayjs.Dayjs | null;

  export function min(
    ...dayjs: [_dayjs.Dayjs, ..._dayjs.Dayjs[]]
  ): _dayjs.Dayjs;
  export function min(...noDates: never[]): null;
  export function min(...maybeDates: _dayjs.Dayjs[]): _dayjs.Dayjs | null;
}

export { dayjs };
export type { Dayjs };
